/* eslint-disable max-lines */
import {TFunction} from "i18next";
import {
  ActionResult,
  Challenge,
  ChallengeMode,
  CompetenceResult,
  EducationSessionResult,
  Maybe,
  ParametricResult,
  ParametricResultDescription,
  ResultDescription,
} from "../../schema";
import {formatDuration} from "../../time";

export function getResultDisplayFields(challengeMode: ChallengeMode) {
  switch (challengeMode) {
    case ChallengeMode.COMPETENCE_TEST:
    case ChallengeMode.CUSTOM_PARAMETERS_TEST:
      return ["time"];
    case ChallengeMode.CHOICE_BASED_STUDY:
    case ChallengeMode.CHOICES_WITH_CUSTOM_SCORES:
    case ChallengeMode.RPG_MODE:
      return ["score", "time"];
    default:
      console.warn("Results: unknown challenge mode");
      return ["score", "time"];
  }
}

export function getPassedOrFailedHelpText(isPassed: boolean, challengeMode: ChallengeMode, t: TFunction) {
  return isPassed
    ? challengeMode === ChallengeMode.CHOICES_WITH_CUSTOM_SCORES ||
      challengeMode === ChallengeMode.CUSTOM_PARAMETERS_TEST
      ? t("components.ResultsDisplay.passed.textAlt")
      : t("components.ResultsDisplay.passed.text")
    : t("components.ResultsDisplay.failed.text");
}

export function getScoresData(
  results: Pick<EducationSessionResult, "score" | "time" | "usedHints">,
  challengeMode: ChallengeMode,
  t: TFunction,
) {
  return [
    {
      field: "time",
      title: t("components.ResultsDisplay.scores.time"),
      getValue: () => (results.time !== undefined ? formatDuration(results.time) : ""),
    },
    {
      field: "score",
      title: t("components.ResultsDisplay.scores.score"),
      getValue: () => {
        const isPercentage =
          challengeMode !== ChallengeMode.CHOICES_WITH_CUSTOM_SCORES &&
          challengeMode !== ChallengeMode.CUSTOM_PARAMETERS_TEST;

        return `${results.score} ${isPercentage ? "" : "%"}`;
      },
    },
    {
      field: "hints",
      title: t("components.ResultsDisplay.scores.hints"),
      getValue: () => `${results.usedHints}`,
    },
  ];
}

type ActionEmotionResults = Pick<
  ActionResult,
  | "emotionAnger"
  | "emotionDisgust"
  | "emotionEnthusiasm"
  | "emotionFear"
  | "emotionGuilt"
  | "emotionJoy"
  | "emotionNeutral"
  | "emotionSadness"
  | "emotionShame"
  | "emotionSurprise"
>;

export function getActionEmotionsData(actionResult: ActionEmotionResults, t: TFunction) {
  const emotions: Array<[keyof ActionEmotionResults, string]> = [
    ["emotionJoy", t("types.UserEmotion.JOY")],
    ["emotionNeutral", t("types.UserEmotion.NEUTRAL")],
    ["emotionEnthusiasm", t("types.UserEmotion.ENTHUSIASM")],
    ["emotionAnger", t("types.UserEmotion.ANGER")],
    ["emotionSurprise", t("types.UserEmotion.SURPRISE")],
    ["emotionSadness", t("types.UserEmotion.SADNESS")],
    ["emotionDisgust", t("types.UserEmotion.DISGUST")],
    ["emotionFear", t("types.UserEmotion.FEAR")],
    ["emotionShame", t("types.UserEmotion.SHAME")],
    ["emotionGuilt", t("types.UserEmotion.GUILT")],
  ];

  const entries = emotions.map(([key, label]) => {
    const score = actionResult[key] ?? 0;
    return {label, score};
  });

  const sortedValues = entries.sort((a, b) => b.score - a.score);

  let sum = 0;

  for (let i = 0; i < 3; i++) {
    sum += sortedValues[i].score;
  }

  const threshold = sum / 6;
  return sortedValues.slice(0, 3).filter((val) => val.score > threshold);
}

type EducationSessionEmotionResults = Pick<
  EducationSessionResult,
  | "generalEmotionAnger"
  | "generalEmotionDisgust"
  | "generalEmotionEnthusiasm"
  | "generalEmotionFear"
  | "generalEmotionGuilt"
  | "generalEmotionJoy"
  | "generalEmotionNeutral"
  | "generalEmotionSadness"
  | "generalEmotionShame"
  | "generalEmotionSurprise"
>;

export function getGeneralEmotionsData(results: EducationSessionEmotionResults, t: TFunction) {
  const emotions: Array<[keyof EducationSessionEmotionResults, string]> = [
    ["generalEmotionJoy", t("types.UserEmotion.JOY")],
    ["generalEmotionNeutral", t("types.UserEmotion.NEUTRAL")],
    ["generalEmotionEnthusiasm", t("types.UserEmotion.ENTHUSIASM")],
    ["generalEmotionAnger", t("types.UserEmotion.ANGER")],
    ["generalEmotionSurprise", t("types.UserEmotion.SURPRISE")],
    ["generalEmotionSadness", t("types.UserEmotion.SADNESS")],
    ["generalEmotionDisgust", t("types.UserEmotion.DISGUST")],
    ["generalEmotionFear", t("types.UserEmotion.FEAR")],
    ["generalEmotionShame", t("types.UserEmotion.SHAME")],
    ["generalEmotionGuilt", t("types.UserEmotion.GUILT")],
  ];

  const entries = emotions
    .map(([key, label]) => {
      const score = results[key];
      return typeof score === "number" && score > 0 ? {label, score} : null;
    })
    .filter(Boolean) as Array<{label: string; score: number}>;

  const sortedValues = entries.sort((a, b) => b.score - a.score);
  return sortedValues.slice(0, 3).filter(({score}) => score !== 0);
}

type Description = Maybe<string> | {parameter: string | undefined; message: Maybe<string> | undefined}[];

export function getProcessedResultDescriptions(
  results: Pick<EducationSessionResult, "competenceResults" | "parametricResults" | "score">,
  resultDescriptions: ResultDescription[],
  parametricResultDescriptions: ParametricResultDescription[],
  challenge:  Pick<Challenge, "mode" | "showResultsForEachParameter">,
): Description {
  const isTestWithCustomScores = challenge.mode === ChallengeMode.CHOICES_WITH_CUSTOM_SCORES;
  const isParametricTest = challenge.mode === ChallengeMode.CUSTOM_PARAMETERS_TEST;
  const isCompetenceTest = challenge.mode === ChallengeMode.COMPETENCE_TEST;
  const byParameter = challenge.showResultsForEachParameter

  if (!isTestWithCustomScores && !isParametricTest && !isCompetenceTest) return null;

  if (!byParameter) {
    const resultsForParameter = isCompetenceTest ? results.competenceResults : results.parametricResults;
    if (
      (isCompetenceTest || isParametricTest) &&
      parametricResultDescriptions &&
      resultsForParameter &&
      resultsForParameter.length > 0
    ) {
      let matched: ParametricResultDescription | null = null;

      const getScore = (result: CompetenceResult | ParametricResult) => {
        if (result.__typename === "CompetenceResult") return result.score * 100;
        else if (result.__typename === "ParametricResult") return result.rawSum;
        return 0;
      };

      const getName = (result: CompetenceResult | ParametricResult) => {
        if (result.__typename === "CompetenceResult") return result.competence;
        else if (result.__typename === "ParametricResult") return result.parameterName;
        return "";
      };

      descriptionsLoop: for (var a = 0; a < parametricResultDescriptions.length; a++) {
        matched = parametricResultDescriptions[a];
        for (var b = 0; b < resultsForParameter.length; b++) {
          for (var c = 0; c < parametricResultDescriptions[a].conditions.length; c++) {
            const condition = parametricResultDescriptions[a].conditions[c];
            const min = condition.minValue;
            const max = condition.maxValue;
            if (min == null || max == null) {
              continue;
            }
            const score = getScore(resultsForParameter[b]);
            const name = getName(resultsForParameter[b]);
            if (condition.parameter?.name === name && (score < min || score > max)) {
              matched = null;
              continue descriptionsLoop;
            }
          }
        }
        return matched.message ?? null;
      }
    }

    if (isTestWithCustomScores && resultDescriptions) {
      return (
        resultDescriptions.find((desc) => desc.minScore <= results.score && desc.maxScore >= results.score)?.message ??
        null
      );
    }

    return null;
  }

  // Parametric Result descriptions
  const paramsWithDescription = parametricResultDescriptions.map((desc) =>
    desc.message &&
      desc.conditions[0] &&
      desc.conditions[0].parameter?.name
        ? desc.conditions[0].parameter.name
        : undefined
  );

  const uniqueParams = Array.from(new Set(paramsWithDescription?.map((item) => item)));

  if (isCompetenceTest && results.competenceResults) {
    return results.competenceResults.map((competenceResult) => {
      const scorePercent = competenceResult.score * 100;
      const matchedDesc = parametricResultDescriptions.find(
        (desc) =>
          desc.conditions[0].parameter?.name === competenceResult.competence &&
          scorePercent >= (desc.conditions[0]?.minValue ?? 0) &&
          scorePercent <= (desc.conditions[0]?.maxValue ?? 100),
      );
      return {
        parameter: competenceResult.competence,
        message: matchedDesc?.message,
      };
    });
  }
  return uniqueParams.map((param) => ({
    parameter: param,
    message: parametricResultDescriptions.find(
      (desc) =>
        desc.conditions[0].parameter?.name === param &&
        results.parametricResults.find(
          (result) =>
            result.parameterName === param &&
            result.rawSum >= desc.conditions[0].minValue! &&
            result.rawSum <= desc.conditions[0].maxValue!,
        ),
    )?.message,
  }));
}

export function getParticipationReportExportUrl(participationId: string, variant: string, urlTemplate: string) {
  return urlTemplate.replace(":participation_id", participationId).replace(":variant", variant);
}

export function getUserBestAttemptsReportExportUrl(userId: string, variant: string, urlTemplate: string) {
  return urlTemplate.replace(":user_id", userId).replace(":variant", variant);
}
